/** The Defending view's press scores. A press served with no score keeps
 *  none: it is never given a stand-in value. */
import { DEFAULT_LENGTH_M } from '../../lib/pitchZones'
import type { ScoreThresholds } from '../../lib/viz/scoreRing'
import { fieldZoneCode } from '../match/filtering'
import type { MomentLike } from './momentUtils'

/** A pressing moment's score: the first of its pressing index and its
 *  score that is a number above 0, else null. A press is found only where
 *  its score reaches a press threshold above 0, on the pressing index or
 *  on the pressing model's score, so a served 0 is a missing score. */
export function pressMomentScore(
  m: { pressing_index?: number | null; score?: number | null },
): number | null {
  for (const s of [m.pressing_index, m.score]) {
    if (typeof s === 'number' && s > 0) return s
  }
  return null
}

/** The bands the Defending ring, its chart and its popovers colour the
 *  presses by. The server's (score_bands) when every scored press is
 *  marked score_scale press_score and carries them. 'none' when a scored
 *  press is marked and the scored presses do not all carry bands: no band
 *  colours them (the neutral state), never another scale's. null when no
 *  scored press is marked: a legacy game's press scores, whose ring and
 *  chart take the pressing index's bands and whose popovers the default
 *  bands. */
export function pressBands(moments: readonly MomentLike[]): ScoreThresholds | 'none' | null {
  const scored = moments.filter((m) => typeof m.score === 'number')
  if (!scored.some((m) => m.score_scale === 'press_score')) return null
  const served = scored.map((m) => (m.score_scale === 'press_score' ? m.score_bands : null))
  return served.every((b) => b) ? served[0]! : 'none'
}

/** The average score of the scored moments, or null when none is scored. */
export function averageScore(moments: readonly MomentLike[]): number | null {
  const scores = moments
    .map((m) => m.score)
    .filter((s): s is number => typeof s === 'number')
  if (scores.length === 0) return null
  return scores.reduce((sum, s) => sum + s, 0) / scores.length
}

/** The presses in each third, [defensive, middle, attacking], in the
 *  pressing team's direction, cut on the game's zone length (the served
 *  zone_length_m — the match page's own; 105 m when it is unknown).
 *
 *  null when the game has no attacking direction: no press can be placed,
 *  and the overlay shows dashes, not a measured 0 0 0 (SDET review of
 *  #946). A press with no x or no half (1 or 2) is in no third, and
 *  `unplaced` counts it — so three counts that sum to less than the
 *  presses on the map say why. */
export function pressThirdCounts(
  presses: readonly MomentLike[], homeAttackDirH1: string | null,
  pressingIsHome: boolean, lengthM: number = DEFAULT_LENGTH_M,
): { thirds: [number, number, number]; unplaced: number } | null {
  if (homeAttackDirH1 !== 'left' && homeAttackDirH1 !== 'right') return null
  const thirds: [number, number, number] = [0, 0, 0]
  let unplaced = 0
  for (const m of presses) {
    const code = typeof m.x === 'number' && (m.phase === 1 || m.phase === 2)
      ? fieldZoneCode(homeAttackDirH1, m.x * 100, pressingIsHome ? 1 : 0, m.phase, lengthM)
      : null
    if (code === 'DEF') thirds[0]++
    else if (code === 'MFD' || code === 'MFA') thirds[1]++
    else if (code === 'ATT') thirds[2]++
    else unplaced++
  }
  return { thirds, unplaced }
}
